import React from 'react';
import { Spin } from 'antd';
import './LoadingVeil.css';

export interface LoadingVeilProps {
    loading: boolean;
    /** read out while loading; the spinner on its own says nothing */
    label?: string;
    /** take the rest of a flex column, for a body that fills its card */
    fill?: boolean;
    /** the spinner's; small for a box only a line or two tall */
    size?: 'small' | 'default' | 'large';
    className?: string;
    children?: React.ReactNode;
}

/**
 * Loading as a veil over the content, never in place of it.
 *
 * Swapping the content for a skeleton or a spinner while it loads gives the box
 * the skeleton's height for a moment and then the content's, and everything
 * below it jumps twice. Here the content stays rendered the whole time: the
 * last answer, or a placeholder of the same shape on the first load. It is
 * blurred, with a spinner over it, so the box is the same size before, during
 * and after.
 *
 * A reload that answers quickly never shows the veil: it comes on after a short
 * delay (see the CSS) and goes off at once. On the first render it is already
 * on, as there is no earlier state for it to transition from.
 *
 * While veiled the content is inert. A figure that is about to change should
 * not take a click, and a screen reader should not read it out as current.
 */
const LoadingVeil = ({ loading, label = 'Loading', fill = false, size = 'default', className, children }: LoadingVeilProps) => (
    <div
        className={['nx-veil', fill && 'nx-veil--fill', className].filter(Boolean).join(' ')}
        data-loading={loading ? 'true' : 'false'}
        aria-busy={loading}
    >
        {/* React 18 does not know `inert`; it passes through as a string */}
        <div className="nx-veil__content" {...(loading ? { inert: '' } as any : {})}>
            {children}
        </div>
        <div className="nx-veil__cover" aria-hidden="true">
            <Spin size={size} />
        </div>
        {loading && <span className="nx-veil__status" role="status">{label}</span>}
    </div>
);

export default LoadingVeil;
